import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useStyles } from './Styles';
import GanttGrid from './GanttGrid';
import {
  FILTER_STATUSES,
  ROW_KIND,
  STATUS_COLOURS,
  ZOOM,
  allExpandableIds,
  buildRowTree,
  filterTreeByStatus,
  unmappedStatuses,
} from './ganttModel';
import {
  fetchGanttCmoOptionsAPI,
  fetchGanttPortfolioAPI,
  fetchGanttSiteOptionsAPI,
} from './api';

/**
 * Gantt Chart (Gantt Chart - Functional Specs).
 *
 * A single interactive timeline of manufacturing activity across the campaign
 * portfolio, with two navigation levels:
 *   Portfolio View  — every Project, quarter-scaled and auto-fit (BR-01)
 *   Campaign View   — one Campaign's Stages and Batches, Month or Week scale
 *
 * This is an in-app full-screen view (not a routed page / no URL), opened and
 * closed by ProductDevelopment through ProjectDevNavContext — the same pattern
 * Batch Analytics and Batch Genealogy already use, so the browser URL never
 * changes and the dashboard's state survives underneath.
 *
 * Composition:
 *   ./ganttModel — every rule (status normalisation, rollups, colour matrix,
 *                  timeline maths). Pure, unit-testable.
 *   ./GanttGrid  — the drawing (header bands, grid, bars, today marker, tooltip)
 *   this file    — the screen: facility selectors, breadcrumb, zoom, status
 *                  chips, expand/collapse, and the two navigation levels
 */

// Portfolio View offers Quarter + Month; Campaign View adds Week (FSD C, BR-03).
const PORTFOLIO_ZOOMS = [ZOOM.QUARTER, ZOOM.MONTH];
const CAMPAIGN_ZOOMS = [ZOOM.QUARTER, ZOOM.MONTH, ZOOM.WEEK];

const GanttChart = ({ onBack, showProgress }) => {
  const classes = useStyles();

  // ── facility selection (FSD A) ────────────────────────────────────────────
  const [cmoOptions, setCmoOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [cmo, setCmo] = useState('all');
  const [site, setSite] = useState('all');

  // ── data ──────────────────────────────────────────────────────────────────
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSynced, setLastSynced] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  // ── view state ────────────────────────────────────────────────────────────
  // null = Portfolio View; otherwise the drilled-into campaign's row id.
  const [campaignRowId, setCampaignRowId] = useState(null);
  const [zoom, setZoom] = useState(ZOOM.QUARTER);
  const [activeStatuses, setActiveStatuses] = useState(() => new Set(FILTER_STATUSES));
  // Expansion is tracked per row id and kept SEPARATELY for the two navigation
  // levels (FSD F Business Rules: "independent between the two navigation
  // levels"), so returning to Portfolio View does not inherit the campaign
  // view's open stages and vice versa.
  const [portfolioExpanded, setPortfolioExpanded] = useState(() => new Set());
  const [campaignExpanded, setCampaignExpanded] = useState(() => new Set());

  // Fetch the facility option lists once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetchGanttCmoOptionsAPI();
      if (!cancelled) setCmoOptions(res.data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  // Site options depend on the selected CMO, so they reload with it — and the
  // site selection resets, since a site from another CMO is not a valid choice.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetchGanttSiteOptionsAPI(cmo);
      if (cancelled) return;
      setSiteOptions(res.data || []);
      setSite('all');
    })();
    return () => { cancelled = true; };
  }, [cmo]);

  // Portfolio data. BR-12 says changing Site does not re-query in this release
  // — that is a statement about the prototype's single static data source, not
  // something to reproduce here: the dashboard feed is genuinely scopeable by
  // CMO and Site, so the chart re-queries and shows the scope the user picked.
  // Doing otherwise would leave the selectors visibly lying about the data.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await fetchGanttPortfolioAPI({ cmoOrgId: cmo, cmoSite: site });
      if (cancelled) return;
      // A status the colour/rollup table does not know is drawn as Planned, so
      // an unnoticed new status quietly understates real work. Name them.
      const unmapped = unmappedStatuses(res.projects);
      if (unmapped.length) {
        console.warn('[GanttChart] Unmapped batch statuses (drawn as Planned):', unmapped);
      }
      setProjects(res.projects || []);
      setLastSynced(new Date());
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [cmo, site, reloadToken]);

  // ── derived rows ──────────────────────────────────────────────────────────
  const fullTree = useMemo(() => buildRowTree(projects), [projects]);

  // Status filtering is applied to the whole tree first (BR-06), so a
  // Campaign's own rollup and span already reflect the active filters before it
  // is drilled into.
  const filteredTree = useMemo(
    () => filterTreeByStatus(fullTree, [...activeStatuses]),
    [fullTree, activeStatuses]
  );

  // The drilled campaign row, looked up in the FILTERED tree so its stages and
  // batches honour the chips too.
  const campaignRow = useMemo(() => {
    if (!campaignRowId) return null;
    for (const project of filteredTree) {
      for (const campaign of project.children || []) {
        if (campaign.id === campaignRowId) return { ...campaign, projectLabel: project.name };
      }
    }
    return null;
  }, [filteredTree, campaignRowId]);

  // A campaign that filtering has removed entirely can no longer be shown —
  // fall back to Portfolio View rather than rendering an empty campaign.
  useEffect(() => {
    if (campaignRowId && !campaignRow) setCampaignRowId(null);
  }, [campaignRowId, campaignRow]);

  // Rows handed to the grid: at Portfolio View the Project tree; in Campaign
  // View the selected campaign's Stage rows (FSD G).
  const gridRows = campaignRow ? campaignRow.children : filteredTree;
  const expanded = campaignRow ? campaignExpanded : portfolioExpanded;

  // Campaign View defaults its Stage rows to expanded (FSD G / BR-11), so only
  // Batch-level detail is toggled there.
  useEffect(() => {
    if (!campaignRow) return;
    setCampaignExpanded(new Set((campaignRow.children || []).map((s) => s.id)));
  }, [campaignRowId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── navigation ────────────────────────────────────────────────────────────
  const drillIntoCampaign = useCallback((row) => {
    if (!row || row.kind !== ROW_KIND.CAMPAIGN) return; // only Campaigns drill (BR-02)
    setCampaignRowId(row.id);
    // Campaign View opens at Month (FSD G Business Rules).
    setZoom(ZOOM.MONTH);
  }, []);

  const backToPortfolio = useCallback(() => {
    setCampaignRowId(null);
    // FSD B: returning to Portfolio View resets zoom to Quarter and clears
    // row-expansion state.
    setZoom(ZOOM.QUARTER);
    setPortfolioExpanded(new Set());
  }, []);

  const toggleExpand = useCallback((id) => {
    const apply = (prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    };
    if (campaignRow) setCampaignExpanded(apply); else setPortfolioExpanded(apply);
  }, [campaignRow]);

  const expandAll = useCallback(() => {
    setPortfolioExpanded(new Set(allExpandableIds(filteredTree)));
  }, [filteredTree]);
  const collapseAll = useCallback(() => setPortfolioExpanded(new Set()), []);

  const toggleStatus = useCallback((status) => {
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status); else next.add(status);
      return next;
    });
  }, []);

  const zoomOptions = campaignRow ? CAMPAIGN_ZOOMS : PORTFOLIO_ZOOMS;
  // Leaving Campaign View while Week is selected would leave Portfolio View on
  // a zoom it does not offer (BR-03), so fall back to Quarter.
  useEffect(() => {
    if (!campaignRow && zoom === ZOOM.WEEK) setZoom(ZOOM.QUARTER);
  }, [campaignRow, zoom]);

  const batchCount = useMemo(() => {
    let n = 0;
    const walk = (r) => {
      if (r.kind === ROW_KIND.BATCH) { n += 1; return; }
      (r.children || []).forEach(walk);
    };
    filteredTree.forEach(walk);
    return n;
  }, [filteredTree]);

  return (
    <div className={classes.container}>
      {/* ── header ── */}
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <div className={classes.backButton} onClick={onBack}>
            <ChevronLeftIcon />
            <Typography variant="body1">Back</Typography>
          </div>
          <Typography className={classes.title}>Gantt Chart</Typography>
        </div>
      </div>

      {/* ── controls: CMO / Site + Last Synced / Refresh (FSD A) above the
          zoom, status chips and expand/collapse strip ── */}
      <div className={classes.controlPanel}>
        <div className={classes.facilityRow}>
          <div>
            <div className={classes.fieldLabel}>CMO</div>
            <FormControl variant="outlined" size="small" className={classes.field}>
              <Select value={cmo} onChange={(e) => setCmo(e.target.value)} displayEmpty>
                <MenuItem value="all">All CMOs</MenuItem>
                {cmoOptions.map((o) => (<MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>))}
              </Select>
            </FormControl>
          </div>
          <div>
            <div className={classes.fieldLabel}>Site / Plant</div>
            <FormControl variant="outlined" size="small" className={classes.field}>
              <Select value={site} onChange={(e) => setSite(e.target.value)} displayEmpty>
                <MenuItem value="all">All sites</MenuItem>
                {siteOptions.map((o) => (<MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>))}
              </Select>
            </FormControl>
          </div>
          <div className={classes.syncWrap}>
            <span className={classes.syncText}>
              {lastSynced
                ? `Last Synced @ ${moment(lastSynced).format('DD-MMM-YYYY HH:mm:ss').toUpperCase()}`
                : 'Syncing…'}
            </span>
            <Button
              className={classes.refreshButton}
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={() => setReloadToken((n) => n + 1)}
              disabled={loading}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* ── toolbar: back-to-portfolio, zoom, status chips, expand/collapse ── */}
        <div className={`${classes.toolbar} ${classes.controlDivider}`}>
          {/* FSD B: a dedicated "← All Projects" button in Campaign view, as a
              second way back to Portfolio View. */}
          {campaignRow && (
            <Button className={classes.textButton} onClick={backToPortfolio}>
              ← All Projects
            </Button>
          )}

          <div className={classes.toolbarGroup}>
            <span className={classes.toolbarLabel}>Zoom</span>
            {zoomOptions.map((z) => (
              <button
                type="button"
                key={z}
                className={`${classes.zoomButton} ${zoom === z ? classes.zoomButtonActive : ''}`}
                onClick={() => setZoom(z)}
              >
                {z}
              </button>
            ))}
          </div>

          <div className={classes.toolbarGroup} style={{ gap: 8 }}>
            {FILTER_STATUSES.map((s) => {
              const on = activeStatuses.has(s);
              const colour = (STATUS_COLOURS[s] || {}).fill;
              return (
                <button
                  type="button"
                  key={s}
                  className={`${classes.chip} ${on ? '' : classes.chipOff}`}
                  onClick={() => toggleStatus(s)}
                  aria-pressed={on}
                >
                  <span className={classes.chipDot} style={{ background: colour }} />
                  {s}
                </button>
              );
            })}
          </div>

          {/* FSD E / BR-11: Expand All / Collapse All is Portfolio View only. */}
          {!campaignRow && (
            <div className={classes.toolbarGroup}>
              <Button className={classes.textButton} onClick={expandAll}>Expand All</Button>
              <Button className={classes.textButton} onClick={collapseAll}>Collapse All</Button>
            </div>
          )}

          <span className={classes.syncText} style={{ marginLeft: 'auto' }}>
            {loading ? '' : `${filteredTree.length} project(s) · ${batchCount} batch(es)`}
          </span>
        </div>
      </div>

      {/* ── breadcrumb (FSD B) — Portfolio View shows none ── */}
      {campaignRow && (
        <div className={classes.breadcrumb}>
          <button type="button" className={classes.crumbLink} onClick={backToPortfolio}>
            {campaignRow.projectLabel}
          </button>
          <span className={classes.crumbSep}>›</span>
          <span className={classes.crumbCurrent}>{campaignRow.name}</span>
        </div>
      )}

      {/* ── chart ── */}
      {loading ? (
        <div className={classes.state}>
          <CircularProgress size={22} />
          <div style={{ marginTop: 12 }}>Loading Gantt Chart…</div>
        </div>
      ) : !fullTree.length ? (
        <div className={classes.state}>
          No dated manufacturing activity for this CMO / site.
        </div>
      ) : (
        <>
          <GanttGrid
            rows={gridRows}
            zoom={zoom}
            expanded={expanded}
            onToggleExpand={toggleExpand}
            onDrillCampaign={drillIntoCampaign}
            campaignRow={campaignRow}
            showProgress={showProgress}
          />
          <div className={classes.legendRow}>
            {FILTER_STATUSES.concat(['Cancelled']).map((s) => (
              <span className={classes.legendItem} key={s}>
                <span className={classes.legendSwatch} style={{ background: (STATUS_COLOURS[s] || {}).fill }} />
                {s}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

GanttChart.propTypes = {
  // Closes the view and returns to the dashboard.
  onBack: PropTypes.func.isRequired,
  // FSD I / BR-09: the in-progress elapsed fill is suppressed entirely when
  // Show Progress is off for the dashboard instance.
  showProgress: PropTypes.bool,
};

GanttChart.defaultProps = {
  showProgress: true,
};

export default GanttChart;
